import React, { useState, useMemo } from "react";
import {
  Box,
  Grid,
  Stack,
  Typography,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  TextField,
  Alert,
  CircularProgress,
} from "@mui/material";

import {
  VerticalBarChart,
  LineChart,
} from "../../../../../shared/components/charts";
import {
  DashboardCard,
  DashboardCardSkeleton,
} from "../../../../../shared/components";
import { DurationSelector, DateRange } from "../../../../../shared/components";
import {
  useMetricsComparison,
  useMetricsTrends,
  usePersistedDateRange,
} from "../../../../../shared/hooks";
import {
  buildFilterParams,
  transformTrendsData,
  calculateGranularity,
} from "../../shared/utils";
import { useClientScope } from "../../../../../shared/contexts";

const MEASURES = [
  "Sleep Score",
  "Overall Stress",
  "Recovery Score",
  "HRV",
  "Strain",
  "Sleep Duration",
  "Resting Heart Rate",
  "Respiratory Rate",
];

const MEASURE_TO_METRIC: Record<string, string> = {
  "Sleep Score": "sleep_score",
  "Overall Stress": "overall_stress",
  "Recovery Score": "recovery_score",
  HRV: "hrv",
  Strain: "strain",
  "Sleep Duration": "sleep_duration",
  "Resting Heart Rate": "resting_heart_rate",
  "Respiratory Rate": "respiratory_rate",
};

const METRIC_TO_MEASURE: Record<string, string> = {
  sleep_score: "Sleep Score",
  overall_stress: "Overall Stress",
  recovery_score: "Recovery Score",
  hrv: "HRV",
  strain: "Strain",
  sleep_duration: "Sleep Duration",
  resting_heart_rate: "Resting Heart Rate",
  respiratory_rate: "Respiratory Rate",
};

const GROUP_BY_OPTIONS = [
  "Department",
  "Age Group",
  "Gender",
  "Shift Pattern",
  "User",
];

const GROUP_BY_TO_API: Record<
  string,
  "department" | "age_group" | "gender" | "shift_pattern" | "user"
> = {
  Department: "department",
  "Age Group": "age_group",
  Gender: "gender",
  "Shift Pattern": "shift_pattern",
  User: "user",
};

const CHART_TYPES = {
  BAR: "bar-comparison",
  TREND: "trend-lines",
} as const;

type ChartType = (typeof CHART_TYPES)[keyof typeof CHART_TYPES];

export const MeasureComparisonInsights: React.FC = () => {
  const theme = useTheme();
  const { effectiveClientId } = useClientScope();
  const { dateRange, setDateRange } = usePersistedDateRange();
  const [primaryMeasure, setPrimaryMeasure] = useState<string>("Sleep Score");
  const [compareWith, setCompareWith] = useState<string>("Overall Stress");
  const [groupBy, setGroupBy] = useState<string>("Department");
  const [chartType, setChartType] = useState<ChartType>(CHART_TYPES.BAR);

  const filterParams = useMemo(
    () =>
      buildFilterParams(
        {
          department: [],
          searchUser: [],
          ageRange: [],
          sex: [],
          shiftPattern: [],
        },
        dateRange,
        effectiveClientId
      ),
    [dateRange, effectiveClientId]
  );

  const granularity = useMemo(
    () => calculateGranularity(dateRange),
    [dateRange]
  );

  const primaryMetric = MEASURE_TO_METRIC[primaryMeasure] || "sleep_score";
  const compareMetric = MEASURE_TO_METRIC[compareWith] || "overall_stress";
  const groupByApi = GROUP_BY_TO_API[groupBy] || "department";

  const {
    data: comparisonData,
    isLoading: comparisonLoading,
    error: comparisonError,
  } = useMetricsComparison({
    ...filterParams,
    group_by: groupByApi,
    metrics: [primaryMetric, compareMetric],
  });

  const {
    data: trendsData,
    isLoading: trendsLoading,
    error: trendsError,
  } = useMetricsTrends({
    ...filterParams,
    metrics: [primaryMetric, compareMetric],
    granularity,
  });

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
  };

  const handleChartTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: ChartType | null
  ) => {
    if (newValue !== null) {
      setChartType(newValue);
    }
  };

  const barChartData = useMemo(() => {
    if (!comparisonData?.data?.groups) return [];
    return comparisonData.data.groups.map((group) => ({
      [groupBy]: group.group_label,
      [primaryMeasure]: group.values[primaryMetric] ?? 0,
      [compareWith]: group.values[compareMetric] ?? 0,
    }));
  }, [
    comparisonData,
    groupBy,
    primaryMeasure,
    compareWith,
    primaryMetric,
    compareMetric,
  ]);

  const trendChartData = useMemo(() => {
    if (!trendsData) return [];
    return transformTrendsData(trendsData, [primaryMetric, compareMetric]).map(
      (point) => ({
        ...point,
        [primaryMeasure]: point[primaryMetric] ?? 0,
        [compareWith]: point[compareMetric] ?? 0,
      })
    );
  }, [trendsData, primaryMeasure, compareWith, primaryMetric, compareMetric]);

  const renderBarComparisonChart = () => {
    if (comparisonLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (comparisonError) {
      return (
        <Alert severity="error">
          Failed to load comparison data. Please try again.
        </Alert>
      );
    }

    if (barChartData.length === 0) {
      return (
        <Alert severity="info">
          No comparison data available for the selected filters.
        </Alert>
      );
    }

    return (
      <VerticalBarChart
        data={barChartData}
        xAxisKey={groupBy}
        series={[
          {
            dataKey: primaryMeasure,
            label: primaryMeasure,
            color: theme.palette.info.main,
          },
          {
            dataKey: compareWith,
            label: compareWith,
            color: theme.palette.success.main,
          },
        ]}
        height={400}
        yAxisMin={0}
      />
    );
  };

  const renderTrendLinesChart = () => {
    if (trendsLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (trendsError) {
      return (
        <Alert severity="error">
          Failed to load trend data. Please try again.
        </Alert>
      );
    }

    if (trendChartData.length === 0) {
      return (
        <Alert severity="info">
          No trend data available for the selected period.
        </Alert>
      );
    }

    return (
      <LineChart
        data={trendChartData}
        xAxisKey="label"
        series={[
          {
            dataKey: primaryMeasure,
            label: primaryMeasure,
            color: theme.palette.info.main,
            area: true,
          },
          {
            dataKey: compareWith,
            label: compareWith,
            color: theme.palette.success.main,
            area: true,
          },
        ]}
        height={400}
        yAxisMin={0}
      />
    );
  };

  const renderChart = () => {
    switch (chartType) {
      case CHART_TYPES.BAR:
        return renderBarComparisonChart();
      case CHART_TYPES.TREND:
        return renderTrendLinesChart();
      default:
        return renderBarComparisonChart();
    }
  };

  const getSubtitle = () => {
    return `Detailed Comparison by ${groupBy}`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <DashboardCard>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: { xs: "wrap", md: "nowrap" },
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                flex: 1,
                flexWrap: { xs: "wrap", md: "nowrap" },
              }}
            >
              <Autocomplete
                options={MEASURES}
                value={primaryMeasure}
                onChange={(_event, newValue) => {
                  if (newValue) setPrimaryMeasure(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Primary Measure"
                    size="small"
                    sx={{ minWidth: 200 }}
                  />
                )}
              />
              <Autocomplete
                options={MEASURES.filter((m) => m !== primaryMeasure)}
                value={compareWith}
                onChange={(_event, newValue) => {
                  if (newValue) setCompareWith(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Compare With"
                    size="small"
                    sx={{ minWidth: 200 }}
                  />
                )}
              />
              <Autocomplete
                options={GROUP_BY_OPTIONS}
                value={groupBy}
                onChange={(_event, newValue) => {
                  if (newValue) setGroupBy(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Group By"
                    size="small"
                    sx={{ minWidth: 200 }}
                  />
                )}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <DurationSelector
                value={dateRange}
                onChange={handleDateRangeChange}
                buttonVariant="outlined"
              />
            </Box>
          </Box>
        </DashboardCard>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            {(comparisonLoading && chartType === CHART_TYPES.BAR) ||
            (trendsLoading && chartType === CHART_TYPES.TREND) ? (
              <DashboardCardSkeleton title subtitle height={450} />
            ) : (
              <DashboardCard
                title="Comparison Visualization"
                subtitle={getSubtitle()}
                titleComponent={
                  <ToggleButtonGroup
                    value={chartType}
                    exclusive
                    onChange={handleChartTypeChange}
                    size="small"
                  >
                    <ToggleButton value={CHART_TYPES.BAR}>
                      Bar Comparison
                    </ToggleButton>
                    <ToggleButton value={CHART_TYPES.TREND}>
                      Trend Lines
                    </ToggleButton>
                  </ToggleButtonGroup>
                }
              >
                {renderChart()}
              </DashboardCard>
            )}
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
